import { activateUser, forgotPassword } from 'src/services/client';
import { toast } from 'react-toastify';
import { IContactFormData, PropertyDetail } from 'src/SharedCustomHooks/UseContactFormData/types';
import { upload } from 'src/helpers/upLoadHelper';
import { IContactFormDataForAPI } from 'src/utils/CommonHelpers/ContactAPIOperations/types';

const handleImageUpload = async (formData: IContactFormData): Promise<string | null> => {
  if (!formData.selectedImage?.length) {
    return formData.profileImage || null;
  }
  
  try {
    return await upload(formData.selectedImage[0]);
  } catch (error) {
    console.error('Image upload failed:', error);
    toast.error('⚠️ Image upload failed, continuing without image.', {
      autoClose: 4000, position: 'top-center',
    });
    return formData.profileImage || null;
  }
};

const transformContactFormDataForAPI = (
  formData: IContactFormData,
  imageUrl: string | null
): IContactFormDataForAPI => {
  const transformedProperties: string[] =
    formData.properties?.map(
      (property: PropertyDetail) => property.propertyId
    ) || [];
  
  const transformed = {
    ...formData,
    profileImage: imageUrl || formData.profileImage,
    properties: transformedProperties,
  };
  
  // Add ServiceProviderAssignedProperties for Service Providers.
  if (formData.userRole === 'Service Provider') {
    const propertyIds =
      formData.properties?.map(
        (property: PropertyDetail) => property.propertyId
      ) || [];
    
    return {
      ...transformed,
      ServiceProviderAssignedProperties: propertyIds,
    };
  }
  
  return transformed;
};

const sendResetPasswordLink = async (
  contactData: IContactFormData
): Promise<void> => {
  try {
    const response: any = await forgotPassword(contactData.email);
    
    if (response.ok || response.status === 200) {
      toast.success(
        ` ⓘ Password reset link has been sent to user ${contactData.firstname} ${contactData.lastname}!`,
        { autoClose: 10000, position: 'top-center' }
      );
    } else {
      toast.error(
        '🚫 Something went wrong on our end. Please try again in a few minutes. If the issue persists, please contact support.',
        {
          autoClose: 4000,
          position: 'top-center',
        }
      );
    }
  } catch (err) {
    console.error('Failed to send password reset link:', err);
    toast.error('🚫 Something went wrong, please try again.', {
      autoClose: 4000,
      position: 'top-center',
    });
  }
};

const sendActivationLink = async (
  contactData: IContactFormData
): Promise<void> => {
  try {
    const response: any = await activateUser({ UserId: contactData.id });
    
    if (response.status === 200 || response.ok) {
      const responseJson = await response.json();
      
      if (responseJson.statusCode === 200) {
        toast.success(
          ` ⓘ Activation link sent to user ${contactData.firstname} ${contactData.lastname}`,
          { autoClose: 10000, position: 'top-center' }
        );
      } else {
        toast.error(
          '🚫 Something went wrong with your request to send an activation email, please try again.',
          {
            autoClose: 4000,
            position: 'top-center',
          }
        );
      }
    } else {
      toast.error('🚫 Something went wrong, please try again.', {
        autoClose: 4000,
        position: 'top-center',
      });
    }
  } catch (err) {
    console.error('Failed to send activation link:', err);
    toast.error(
      '🚫 Something went wrong on our end. Please try again in a few minutes. If the issue persists, please contact support.',
      {
        autoClose: 4000,
        position: 'top-center',
      }
    );
  }
};

const handleContactsAPIResponse = async (
  response: any,
  isUpdate: boolean,
  setResponse: (response: any) => void,
  setError: (error: string | null) => void
): Promise<boolean> => {
  const responseData = await response.json();
  const apiMessage = responseData?.message;

  if (response.status === 200 || response.status === 201) {
    const successMsg = apiMessage || (isUpdate ? 'Contact updated' : 'Contact added');

    toast.success(`ⓘ ${successMsg}`, {
      autoClose: 10000,
      position: 'top-center',
    });

    setResponse(responseData);
    return true;
  }

  if (response.status === 400 || response.status === 409 || response.status === 422) {
    const errorMsg = apiMessage || (response.status === 409
      ? 'A conflict occurred: This contact may already exist.'
      : 'The request was invalid. Please check your input.');

    toast.error(`🚫 ${errorMsg}`, {
      autoClose: 12000,
      position: 'top-center',
    });

    setError(errorMsg);

    return false;
  }

  if (response.status >= 500) {
    const serverError = "Internal Server Error. Please try again later.";
    toast.error(`⚠️ ${serverError}`, {
      autoClose: 12000,
      position: 'top-center',
    });
    setError(serverError);

    return false;
  }

  throw new Error(`Unexpected response status: ${response.status}`);
};

export {
  handleImageUpload,
  transformContactFormDataForAPI,
  sendResetPasswordLink,
  sendActivationLink,
  handleContactsAPIResponse
};